import { type KeyboardEvent, type PointerEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useElementWidth } from '@/hooks/useElementWidth';
import { formatDateDisplay } from '@/utils/date';

import type { VisibleDays } from '../useRailView';

import {
    indexAtFraction,
    labelIndices,
    labelWidthPx,
    shortDayLabel,
    spansYears,
    tickPercent,
} from './dayScrubberLayout';

import styles from './DayScrubber.module.scss';

export interface DayScrubberProps {
    days: { day: string; count: number }[];
    /** The day of the image on screen. */
    currentDay?: string;
    /** The days with cards in view in the strip, which the highlighted window spans. */
    visibleDays: VisibleDays | null;
    onSelectDay: (day: string) => void;
}

/** PageUp and PageDown move a week's worth of days with images. */
const PAGE_STEP = 7;

/** Near either end the drag bubble hangs inwards, so the viewer's edge does not clip it. */
function bubbleAlign(percent: number): 'start' | 'centre' | 'end' {
    if (percent < 20) {
        return 'start';
    }
    return percent > 80 ? 'end' : 'centre';
}

/**
 * The day timeline under the strip: one tick per day with images, and a window over the days
 * the strip shows. Clicking or dragging picks the day under the pointer when released, and the
 * arrow keys, Home, End, PageUp and PageDown step through the days while it has focus.
 *
 * A picked day stays shown as current until that day's images arrive, so the control does not
 * spring back while they load.
 */
export default function DayScrubber(props: DayScrubberProps) {
    const { days, currentDay, visibleDays, onSelectDay } = props;
    const { t } = useTranslation();

    const [rail, railRef, width] = useElementWidth<HTMLDivElement>();
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [picked, setPicked] = useState<{ day: string; from: string | undefined } | null>(null);
    // A filter that empties the strip leaves no current image; keep showing the day it was on.
    const [lastDay, setLastDay] = useState(currentDay);
    if (currentDay !== undefined && currentDay !== lastDay) {
        setLastDay(currentDay);
    }

    const indexOf = useMemo(() => new Map(days.map((entry, index) => [entry.day, index])), [days]);
    const count = days.length;
    const pickedPending =
        picked !== null && picked.from === currentDay && picked.day !== currentDay;
    const shownDay = pickedPending ? picked.day : (currentDay ?? lastDay);
    const valueIndex = indexOf.get(shownDay ?? '') ?? count - 1;

    const withYear = spansYears(days);
    const labels = useMemo(
        () => labelIndices(count, width, labelWidthPx(withYear)),
        [count, width, withYear]
    );

    if (count === 0) {
        return null;
    }

    const valueText = (index: number) =>
        t('imageViewer.dayScrubber.valueText', {
            count: days[index].count,
            date: formatDateDisplay(days[index].day),
        });

    const select = (index: number) => {
        const day = days[Math.min(count - 1, Math.max(0, index))].day;
        setPicked({ day, from: currentDay });
        onSelectDay(day);
    };

    const indexAtPointer = (event: PointerEvent<HTMLElement>) => {
        const rect = rail?.getBoundingClientRect();
        if (!rect || rect.width === 0) {
            return valueIndex;
        }
        return indexAtFraction((event.clientX - rect.left) / rect.width, count);
    };

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragIndex(indexAtPointer(event));
    };
    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (dragIndex !== null) {
            setDragIndex(indexAtPointer(event));
        }
    };
    const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
        if (dragIndex === null) {
            return;
        }
        setDragIndex(null);
        select(indexAtPointer(event));
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const steps: Record<string, number> = {
            ArrowLeft: valueIndex - 1,
            ArrowDown: valueIndex - 1,
            ArrowRight: valueIndex + 1,
            ArrowUp: valueIndex + 1,
            PageDown: valueIndex - PAGE_STEP,
            PageUp: valueIndex + PAGE_STEP,
            Home: 0,
            End: count - 1,
        };
        const target = steps[event.key];
        if (target === undefined) {
            return;
        }
        event.preventDefault();
        const next = Math.min(count - 1, Math.max(0, target));
        if (next !== valueIndex) {
            select(next);
        }
    };

    const visibleFrom = visibleDays ? indexOf.get(visibleDays.first) : undefined;
    const visibleTo = visibleDays ? indexOf.get(visibleDays.last) : undefined;
    const [windowFrom, windowTo] =
        dragIndex !== null
            ? [dragIndex, dragIndex]
            : !pickedPending && visibleFrom !== undefined && visibleTo !== undefined
              ? [Math.min(visibleFrom, visibleTo), Math.max(visibleFrom, visibleTo)]
              : [valueIndex, valueIndex];

    const windowLeft = tickPercent(windowFrom, count);
    const windowWidth = tickPercent(windowTo, count) - windowLeft;

    return (
        <div
            className={classNames(styles.dayScrubber, { [styles.dragging]: dragIndex !== null })}
            role="slider"
            tabIndex={0}
            aria-label={t('molecules.imageTimeline.label')}
            aria-valuemin={0}
            aria-valuemax={count - 1}
            aria-valuenow={valueIndex}
            aria-valuetext={valueText(valueIndex)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => setDragIndex(null)}
            onKeyDown={onKeyDown}>
            <div className={styles.rail} ref={railRef}>
                <span className={styles.bar} />
                {days.map((entry, index) => (
                    <span
                        key={entry.day}
                        className={styles.tick}
                        data-day={entry.day}
                        data-in-window={index >= windowFrom && index <= windowTo}
                        style={{ left: `${tickPercent(index, count)}%` }}
                    />
                ))}
                <span
                    className={styles.window}
                    // Padded past the end ticks so a single day still reads as a window.
                    style={{
                        left: `calc(${windowLeft}% - 6px)`,
                        width: `calc(${windowWidth}% + 12px)`,
                    }}
                />
                {dragIndex !== null && (
                    <span
                        className={styles.bubble}
                        data-align={bubbleAlign(tickPercent(dragIndex, count))}
                        style={{ left: `${tickPercent(dragIndex, count)}%` }}
                        aria-hidden="true">
                        {valueText(dragIndex)}
                    </span>
                )}
            </div>
            <div className={styles.labels} aria-hidden="true">
                {labels.map((index, position) => (
                    <span
                        key={days[index].day}
                        className={styles.label}
                        data-align={
                            position === 0 && labels.length > 1
                                ? 'start'
                                : position === labels.length - 1 && labels.length > 1
                                  ? 'end'
                                  : 'centre'
                        }
                        style={{ left: `${tickPercent(index, count)}%` }}>
                        {shortDayLabel(days[index].day, withYear)}
                    </span>
                ))}
            </div>
        </div>
    );
}
